// ============================================================================
// AFB-0075 / PR 4 — a field layout read from the PDF's OWN form fields.
//
// Antonela's onboarding packets are not flat scans. They are AcroForm PDFs:
// the base packet declares 107 widgets and the Colorado one 154, and every one
// of them carries a /Rect the document itself asserts. Hand-placing 107 boxes
// on top of geometry the file already publishes is transcription — and
// transcribing coordinates onto something somebody then signs is the kind of
// work that is wrong two percent of the time and impossible to spot
// afterwards.
//
// So: read the declared geometry, propose a layout from it, and let her REVIEW
// and adjust instead of place from scratch. This is not field-detect.ts or
// anchor-placement.ts, both of which guess from where text happens to sit.
// This is the document's own answer to "where is this box".
//
// ONE REFUSAL AND ONE RESTRICTION SIT ABOVE ALL OF THAT, and they are the point
// of the file as much as the arithmetic is. A widget whose name says NOTHING is
// refused, because an unnamed widget cannot be classified at all. A widget
// whose name is SENSITIVE is placed — as a signer field, never a merge field,
// flagged is_sensitive so its answer is routed to esign_field_secrets rather
// than to the readable column. See THE RESTRICTED RULE below.
//
// Pure, for the same reason template-bindings.ts and packet-composition.ts
// are: envelope-lib.ts imports pdfjs-dist at load time, which needs DOMMatrix,
// so anything importing it cannot be loaded by a node test AT ALL. pdf.js does
// the reading over in envelope-lib (extractAcroFormWidgets); every rule, every
// skip and all of the arithmetic lives here, where it can be pinned.
//
// ── THE COORDINATE CONTRACT ─────────────────────────────────────────────────
//
// Stored coordinates are fractions of the page in [0,1] with a TOP-LEFT
// origin, because that is what envelope-prep.tsx reads back when it draws:
//
//     left = position_x * pageW ;  top = position_y * pageH
//
// A PDF /Rect is user space: BOTTOM-left origin, and measured from the
// viewBox origin rather than from zero — a viewBox of [0,0,612,792] is the
// common case, not the contract. Both differences are handled once, here, in
// widgetBox():
//
//     pw = vb[2] - vb[0]           ph = vb[3] - vb[1]
//     x  = (left  - vb[0]) / pw    y  = (vb[3] - top) / ph
//     w  = (right - left)  / pw    h  = (top - bottom) / ph
//
// The y line is the whole file in one expression: a widget at the TOP of the
// page has a LARGE PDF y and must come out with a SMALL fraction.
//
// A ROTATED page is REFUSED rather than transformed. The four rotation cases
// are individually easy and collectively easy to get subtly wrong, and being
// wrong costs a signature box in the wrong place on a legal document. A page
// handed back is a page Antonela places by hand — which is exactly what she
// does today, so a refusal is never worse than the status quo, whereas a
// silently wrong /Rect is much worse than it.
// ============================================================================

import type {
  FieldType, TemplateField, TemplateRecipientRole,
} from './envelope-lib';

/** One page's geometry, exactly as `page.getViewport({ scale: 1 })` reports it. */
export interface AcroFormPage {
  /** 1-based, matching TemplateField.page. */
  page: number;
  /** vp.viewBox — [x0, y0, x1, y1] in PDF user space. */
  viewBox: number[];
  /** vp.rotation in degrees. Anything but a multiple of 360 is unhandled. */
  rotation: number;
}

/** One /Widget annotation, flattened to plain data so this file stays pure. */
export interface AcroFormWidget {
  page: number;
  /** The PDF's own field name. '' when the document does not give one. */
  fieldName: string;
  /** 'Tx' | 'Btn' | 'Ch' | 'Sig'. Anything else is a widget we do not place. */
  fieldType: string;
  /** /Rect — [ax1, ay1, ax2, ay2] in PDF user space, corners in either order. */
  rect: number[];
  readOnly?: boolean;
  hidden?: boolean;
  radioButton?: boolean;
  checkBox?: boolean;
  pushButton?: boolean;
}

export interface AcroFormPlacementInput {
  pages: AcroFormPage[];
  widgets: AcroFormWidget[];
  /**
   * The template's recipient_roles. Only role_key and role are read, so a full
   * TemplateRecipientRole[] off the row can be passed straight in.
   */
  roles: Array<Pick<TemplateRecipientRole, 'role_key' | 'role'>> | null;
  /**
   * binding_key of every ACTIVE, non-sensitive esign_field_bindings row — i.e.
   * selectableBindings(). A binding not on this list is never proposed.
   */
  bindingKeys: string[];
}

/**
 * Widgets dropped for one reason, counted rather than listed.
 *
 * `pages` turns the count into a task. "3 skipped — unnamed" is a footnote
 * somebody scrolls past; "3 to place by hand, on pages 1 and 2" is a job. It
 * matters here more than it looks: on the real Direct Deposit form the ACCOUNT
 * NUMBER box is one of the unnamed three, and it has to be placed by hand and
 * then ticked Restricted.
 */
export interface SkippedGroup {
  reason: string;
  count: number;
  /** Ascending, de-duplicated. The pages the dropped widgets sat on. */
  pages: number[];
}

export interface AcroFormPlacementResult {
  /** The proposed layout. NOT saved — the caller shows it first. */
  fields: TemplateField[];
  /**
   * Field names of the widgets that were PLACED as restricted — is_sensitive,
   * signer-filled, never bound. Named `restrictedFields` and not
   * `skippedSensitive` because nothing here is skipped: these boxes are on the
   * document and the employee fills them. What is restricted is who may read
   * the answer afterwards.
   */
  restrictedFields: string[];
  /** Everything that was dropped, grouped by why. */
  skippedOther: SkippedGroup[];
  /** Pages whose geometry could not be trusted, so nothing on them was placed. */
  unhandledPages: number[];
  /** Why nothing at all could be generated. Empty ⇒ the run was possible. */
  blockers: string[];
}

/** Why a widget was dropped. User-facing: these are read out in the summary. */
export const SKIP_REASONS = {
  hidden: 'hidden in the PDF',
  pushButton: 'a push button — nothing is ever filled into one',
  zeroArea: 'zero-sized on the page',
  offPage: 'positioned outside the page',
  unsupported: 'a widget type this cannot place',
  unnamed:
    'unnamed, or named something that carries no meaning, so there is no way to '
    + 'tell what the box is for',
  noPage: 'on a page that could not be read',
} as const;

/** Fixed report order, so a re-run does not reshuffle the summary. */
const SKIP_ORDER: string[] = [
  SKIP_REASONS.hidden,
  SKIP_REASONS.pushButton,
  SKIP_REASONS.zeroArea,
  SKIP_REASONS.offPage,
  SKIP_REASONS.unsupported,
  SKIP_REASONS.unnamed,
  SKIP_REASONS.noPage,
];

// ── name normalisation ──────────────────────────────────────────────────────
//
// AcroForm field names are written by whoever built the form, so the same idea
// arrives as "SSN", "ssn_1", "Employee SSN", "EmployeeSSN" and "Social
// Security #". Two views of a name are enough to handle all of them.

/** Lowercase, letters and digits only. "Social Security #" → "socialsecurity". */
export function flattenFieldName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * The name split into words — on separators AND on camelCase boundaries, so
 * "EmployeeSSN", "employee_ssn" and "Employee SSN" all give ['employee','ssn'].
 */
export function fieldNameWords(name: string): string[] {
  return name
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w !== '');
}

/**
 * Names the authoring tool made up, which say nothing about the box.
 *
 * FOUND IN PRODUCTION. On Direct Deposit.pdf the widget directly below "9Digit
 * Routing" — the BANK ACCOUNT NUMBER box, at x 0.3349 / y 0.6550 — is named
 * literally "undefined".
 *
 * THIS SKIP SURVIVED THE 2026-08-20 REVERSAL, and for a reason that has nothing
 * to do with sensitivity. Restricted answers are now collected, so "undefined"
 * is no longer dangerous because it might be a bank account. It is still
 * useless because it might be ANYTHING: a widget with no meaningful name cannot
 * be typed, cannot be bound, cannot be labelled for the signer and cannot be
 * classified as restricted or ordinary. There is nothing to propose. Antonela
 * places it by hand on the prep screen, where she can see the page.
 *
 * Kept to the exact spellings seen on Antonela's forms rather than widened to
 * "text1", "field2" and friends: an Acrobat-authored packet can name EVERY
 * widget that way, and refusing all of them would leave 107 boxes to place by
 * hand — the problem this feature exists to remove. If a form turns up that
 * needs it, this array is the one place to change.
 */
const MEANINGLESS_NAMES = ['undefined', 'untitled', 'text', 'field'];

/** True when this "name" tells us nothing, so the widget must not be placed. */
export function isMeaninglessFieldName(name: string): boolean {
  const trimmed = name.trim().toLowerCase();
  if (trimmed === '') return true;
  if (MEANINGLESS_NAMES.includes(trimmed)) return true;
  // Bare ordinals — "0", "1", "2" — which that same form also uses.
  return /^[0-9]+$/.test(trimmed);
}

// ── THE RESTRICTED RULE ─────────────────────────────────────────────────────
//
// Ben's ruling, corrected 2026-08-20 from an earlier over-reading of it:
//
//   * The company NEVER puts an SSN, a date of birth, a bank account or a
//     routing number onto a form ahead of time. No merge binding, ever — which
//     is why esign_field_bindings marks those keys is_sensitive and
//     selectableBindings() never offers one.
//   * The employee MUST be able to fill them in and send them back. Payroll
//     cannot start otherwise. Refusing to collect an SSN does not protect the
//     employee; it just means the W-4 is filled in on paper, photographed, and
//     emailed — which is worse in every direction.
//   * What comes back is RESTRICTED: Antonela (role `hr`) and executives.
//
// So a sensitive widget is PLACED, and placed deliberately narrowly:
//
//     field_class: 'signer'   — the employee types it, nothing prefills it
//     binding:     null       — there is no binding that could reach it
//     is_sensitive: true      — routes the answer to esign_field_secrets
//
// That last flag is what makes the rest safe. esign_fields carries
// CHECK (NOT (is_sensitive AND value IS NOT NULL)), so the readable table is
// INCAPABLE of holding one of these answers; the edge function writes it to
// esign_field_secrets, whose RLS grants SELECT only to can_view_restricted_hr()
// and grants `authenticated` no write at all.
//
// THE EARLIER VERSION OF THIS FILE SKIPPED THESE ENTIRELY, and that was wrong.
// It read "do not store it" as "do not collect it" and left Antonela with a
// W-4 that had no SSN box on it. If you are here to restore that skip: the
// storage question is already answered — read the two paragraphs above and
// esign_field_secrets before changing anything.
//
// The names still come back to the screen, now as restrictedFields, so Antonela
// can see which answers will be visible only to her and the executives.
const SENSITIVE_PHRASES = [
  'socialsecurity',
  'dateofbirth',
  'birthdate',
  'bankaccount',
  'accountnumber',
  'accountholder',
  'acctnum',
  'nameofbank',
  'bankname',
  'routing',
  'sortcode',
  'swift',
  'taxid',
  // "Driver's License" and "Driver License" are the same document; the second
  // spelling is the official one in several states, so both are listed rather
  // than relying on the possessive being typed.
  'driverslicense',
  'driverlicense',
  'passport',
];

/**
 * The three- and four-letter ones, matched as WHOLE WORDS only.
 *
 * As substrings they are landmines: "Alabama" contains "aba", "protein"
 * contains "ein", and "acct" would fire on anything shaped like "accthing".
 * Over-matching is the safe direction for the ruling but not for the document —
 * a field wrongly flagged restricted has its answer locked away from the
 * managers who need it, and nothing on screen says why.
 *
 * The compound spellings that DO want a substring match ("acctnum", "iban_no")
 * are covered above and by the word split, which turns "AcctNum" and
 * "acct_number" into ['acct', …] anyway.
 */
const SENSITIVE_WORDS = ['ssn', 'dob', 'aba', 'ein', 'acct', 'iban'];

/**
 * True when the answer to this widget is restricted to HR and executives.
 *
 * NOT a refusal to place. The field is placed as a signer field with
 * is_sensitive set — see THE RESTRICTED RULE above.
 */
export function isSensitiveFieldName(name: string): boolean {
  const flat = flattenFieldName(name);
  if (SENSITIVE_PHRASES.some((p) => flat.includes(p))) return true;
  const words = fieldNameWords(name);
  return SENSITIVE_WORDS.some((w) => flat === w || words.includes(w));
}

// ── field type ──────────────────────────────────────────────────────────────

/** Short and obvious on purpose: a miss is a text box she retypes as a date. */
const DATE_WORDS = ['date', 'dates', 'dated'];
const INITIALS_WORDS = ['initial', 'initials', 'inits'];

/**
 * The e-sign field type for a widget, or null when there is nothing to place.
 *
 * A push button is filtered before this — it is a skip with its own count, not
 * an unrecognised type.
 */
export function widgetFieldType(w: AcroFormWidget): FieldType | null {
  if (w.fieldType === 'Sig') return 'signature';
  if (w.fieldType === 'Btn') {
    return w.checkBox === true || w.radioButton === true ? 'checkbox' : null;
  }
  // A choice list is still typed text once it is on our overlay; we do not
  // carry the PDF's option list, so offering a dropdown would be a lie.
  if (w.fieldType === 'Ch') return 'text';
  if (w.fieldType !== 'Tx') return null;
  const words = fieldNameWords(w.fieldName);
  if (words.some((x) => INITIALS_WORDS.includes(x))) return 'initials';
  if (words.some((x) => DATE_WORDS.includes(x))) return 'date';
  return 'text';
}

// ── binding inference ───────────────────────────────────────────────────────
//
// Conservative by construction. captureLayout refuses to guess bindings at all
// and says why: "guessing 'this looks like a name field, bind it to
// employee.name_full' would put a value the sender never chose onto a document
// somebody then signs." That reasoning still holds for a captured layout, where
// nothing is reviewed. It is relaxed here — and ONLY here — because this run
// ends in a review screen before anything is written, and because the name
// being matched is the document author's own declared field name rather than a
// scrap of nearby text.
//
// The rules are ordered and FIRST MATCH WINS, most specific first: "Email
// Address" is an email, not an address.

interface BindingRule {
  /** Phrases to look for. A one-word phrase must match a whole word. */
  phrases: string[];
  binding: string;
}

const BINDING_RULES: BindingRule[] = [
  { phrases: ['first name', 'given name'], binding: 'employee.name_first' },
  { phrases: ['last name', 'surname', 'family name'], binding: 'employee.name_last' },
  {
    phrases: ['full name', 'employee name', 'printed name', 'print name'],
    binding: 'employee.name_full',
  },
  { phrases: ['start date', 'date started'], binding: 'onboarding.start_date' },
  { phrases: ['hire date', 'date of hire', 'date hired'], binding: 'employee.hire_date' },
  { phrases: ['email'], binding: 'employee.email_personal' },
  { phrases: ['phone'], binding: 'employee.phone' },
  { phrases: ['zip', 'zip code', 'postal code'], binding: 'employee.zip' },
  { phrases: ['city'], binding: 'employee.city' },
  { phrases: ['state'], binding: 'employee.state' },
  {
    phrases: ['street address', 'home address', 'mailing address', 'street', 'address'],
    binding: 'employee.street_address',
  },
  { phrases: ['job title', 'position', 'title'], binding: 'employee.title' },
  {
    phrases: ['company', 'company name', 'legal name', 'employer name'],
    binding: 'company.legal_name',
  },
];

/**
 * Does this field name contain `phrase`?
 *
 * A MULTI-WORD phrase also matches the flattened name, so "FirstName",
 * "first_name" and "First Name" all hit. A ONE-WORD phrase matches only as a
 * whole word, never as a substring: "Capacity" contains "city" and "Statement"
 * contains "state", and a merge binding is a value printed onto a document
 * somebody signs.
 */
function nameMatchesPhrase(flat: string, words: string[], phrase: string): boolean {
  const parts = phrase.split(' ');
  if (parts.length === 1) return words.includes(parts[0]);
  if (flat.includes(parts.join(''))) return true;
  for (let i = 0; i + parts.length <= words.length; i += 1) {
    if (parts.every((p, k) => words[i + k] === p)) return true;
  }
  return false;
}

/**
 * The binding this field name confidently means, or null.
 *
 * A rule that matches but names a binding this deployment does not have
 * (retired, renamed, never seeded) proposes NOTHING rather than falling through
 * to a vaguer rule: a merge field pointing at an absent key is a resolution gap
 * on a signed document, which templatePacketBlockers would then have to report.
 */
export function inferBinding(name: string, bindingKeys: string[]): string | null {
  const flat = flattenFieldName(name);
  const words = fieldNameWords(name);
  for (const rule of BINDING_RULES) {
    if (!rule.phrases.some((p) => nameMatchesPhrase(flat, words, p))) continue;
    return bindingKeys.includes(rule.binding) ? rule.binding : null;
  }
  return null;
}

// ── geometry ────────────────────────────────────────────────────────────────

export type WidgetBox =
  | { ok: true; x: number; y: number; width: number; height: number }
  | { ok: false; reason: string };

/** Rounded the way onDragEnd rounds, so a generated box and a dragged one match. */
function round4(v: number): number {
  return Number(v.toFixed(4));
}

function clamp01(v: number): number {
  return Math.min(Math.max(v, 0), 1);
}

/**
 * Slop tolerated before a rect counts as off the page: 0.1% of it, about
 * half a point on US Letter. Border widths and rounding routinely push a /Rect
 * a hair past the media box; a widget genuinely off the page is a different
 * thing and is refused rather than dragged back into view.
 */
const OFF_PAGE_TOLERANCE = 0.001;

/** One /Rect turned into the stored, top-left, normalised box. */
export function widgetBox(rect: number[], viewBox: number[]): WidgetBox {
  if (viewBox.length < 4 || rect.length < 4) {
    return { ok: false, reason: SKIP_REASONS.noPage };
  }
  const [vx0, vy0, vx1, vy1] = viewBox;
  const pw = vx1 - vx0;
  const ph = vy1 - vy0;
  if (!(pw > 0) || !(ph > 0)) return { ok: false, reason: SKIP_REASONS.noPage };

  const [ax1, ay1, ax2, ay2] = rect;
  if (![ax1, ay1, ax2, ay2].every((n) => Number.isFinite(n))) {
    return { ok: false, reason: SKIP_REASONS.offPage };
  }
  // /Rect corners come in either order; the spec does not promise lower-left
  // first and plenty of generators do not oblige.
  const left = Math.min(ax1, ax2);
  const right = Math.max(ax1, ax2);
  const bottom = Math.min(ay1, ay2);
  const top = Math.max(ay1, ay2);
  if (right - left <= 0 || top - bottom <= 0) {
    return { ok: false, reason: SKIP_REASONS.zeroArea };
  }

  const rawX = (left - vx0) / pw;
  // The flip. PDF y is measured UP from the bottom of the viewBox; a stored y
  // is measured DOWN from the top. Top of the page ⇒ large PDF y ⇒ small y.
  const rawY = (vy1 - top) / ph;
  const rawW = (right - left) / pw;
  const rawH = (top - bottom) / ph;

  if (
    rawX < -OFF_PAGE_TOLERANCE
    || rawY < -OFF_PAGE_TOLERANCE
    || rawX + rawW > 1 + OFF_PAGE_TOLERANCE
    || rawY + rawH > 1 + OFF_PAGE_TOLERANCE
  ) {
    return { ok: false, reason: SKIP_REASONS.offPage };
  }

  const x = clamp01(rawX);
  const y = clamp01(rawY);
  return {
    ok: true,
    x: round4(x),
    y: round4(y),
    width: round4(Math.min(clamp01(rawW), 1 - x)),
    height: round4(Math.min(clamp01(rawH), 1 - y)),
  };
}

// ── the run ─────────────────────────────────────────────────────────────────

/**
 * Said when the template has nobody to place fields FOR.
 *
 * Nothing is generated in that case rather than generating with a null
 * role_key: a layout whose entries name no role cannot be resolved to a
 * recipient at send time, so it would look like 107 fields of progress and
 * place none of them.
 */
export const NO_SIGNER_BLOCKER =
  'This template has no signing role yet, so there is nobody to place the fields for. '
  + 'Add a role under "Who signs it" with the type SIGNER, save it, then run this again.';

/**
 * Propose a field_layout from the widgets the PDF declares.
 *
 * Nothing here writes anything. The result is shown to the sender first — the
 * counts, the refusals and the pages that were left alone are all part of the
 * output for that reason, not as diagnostics.
 */
export function placeAcroFormFields(
  input: AcroFormPlacementInput,
): AcroFormPlacementResult {
  const { pages, widgets, roles, bindingKeys } = input;

  const nothing: AcroFormPlacementResult = {
    fields: [],
    restrictedFields: [],
    skippedOther: [],
    unhandledPages: [],
    blockers: [],
  };

  // recipient_index is an index into the NON-CC recipients, because that is
  // what captureLayout writes and what applyLayout reads back. Matching it
  // here means a generated layout and a captured one are the same shape.
  const signers = (roles ?? []).filter((r) => r.role !== 'CC');
  const signerIndex = signers.findIndex(
    (r) => r.role === 'SIGNER' && r.role_key.trim() !== '',
  );
  if (signerIndex === -1) {
    return { ...nothing, blockers: [NO_SIGNER_BLOCKER] };
  }
  const roleKey = signers[signerIndex].role_key.trim();

  const usable = new Map<number, AcroFormPage>();
  const unhandled = new Set<number>();
  for (const p of pages) {
    const pw = (p.viewBox[2] ?? 0) - (p.viewBox[0] ?? 0);
    const ph = (p.viewBox[3] ?? 0) - (p.viewBox[1] ?? 0);
    // rotation % 360 rather than !== 0 so a page stamped 360 is not refused for
    // being described the long way round.
    if (p.rotation % 360 !== 0 || p.viewBox.length < 4 || !(pw > 0) || !(ph > 0)) {
      unhandled.add(p.page);
      usable.delete(p.page);
      continue;
    }
    usable.set(p.page, p);
  }

  const fields: TemplateField[] = [];
  const restricted: string[] = [];
  const counts = new Map<string, number>();
  const skipPages = new Map<string, Set<number>>();
  const note = (reason: string, page: number) => {
    counts.set(reason, (counts.get(reason) ?? 0) + 1);
    const seen = skipPages.get(reason) ?? new Set<number>();
    if (Number.isFinite(page)) seen.add(page);
    skipPages.set(reason, seen);
  };

  for (const w of widgets) {
    // Already reported as a whole page. Counting each of its widgets again
    // would say the same thing twice in two different units.
    if (unhandled.has(w.page)) continue;
    const page = usable.get(w.page);
    if (!page) { note(SKIP_REASONS.noPage, w.page); continue; }
    if (w.hidden === true) { note(SKIP_REASONS.hidden, w.page); continue; }
    if (w.pushButton === true) { note(SKIP_REASONS.pushButton, w.page); continue; }

    const name = (w.fieldName ?? '').trim();
    // A widget with no name — or with a name the authoring tool invented, like
    // the literal "undefined" over the account-number box on Direct Deposit.pdf
    // — says nothing about what the box is for, so there is nothing to propose
    // and it is left for the prep screen. Still right after the reversal, for a
    // different reason: see MEANINGLESS_NAMES.
    if (isMeaninglessFieldName(name)) { note(SKIP_REASONS.unnamed, w.page); continue; }

    const isRestricted = isSensitiveFieldName(name);

    const field_type = widgetFieldType(w);
    if (field_type === null) { note(SKIP_REASONS.unsupported, w.page); continue; }

    const box = widgetBox(w.rect, page.viewBox);
    if (!box.ok) { note(box.reason, w.page); continue; }

    // Only a text-ish widget can carry a binding. A signature or a checkbox is
    // the signer's act, and prefilling either is not a merge, it is a forgery.
    //
    // A RESTRICTED field never carries one either, whatever its name looks
    // like: there is no binding that reaches an SSN (selectableBindings filters
    // is_sensitive out of the registry), and a merge field is by definition a
    // value the company put on the form ahead of time — the one thing the
    // ruling forbids outright.
    const binding = !isRestricted && (w.fieldType === 'Tx' || w.fieldType === 'Ch')
      ? inferBinding(name, bindingKeys)
      : null;

    // Reported once per NAME, not once per widget: a radio group is many
    // widgets and one thing for Antonela to be told about. Recorded HERE rather
    // than at the match, so a restricted widget that was skipped for being
    // hidden or zero-sized is not announced as a field she will be able to see.
    if (isRestricted && !restricted.includes(name)) restricted.push(name);

    fields.push({
      recipient_index: signerIndex,
      field_type,
      page: w.page,
      position_x: box.x,
      position_y: box.y,
      width: box.width,
      height: box.height,
      // Only a signature is required off the bat. Marking 107 boxes required
      // would stop the signer submitting until every optional line on an
      // onboarding form is filled; she can raise the rest in review. A
      // read-only widget is never required — the document itself says nobody
      // was meant to type in it.
      required: field_type === 'signature' && w.readOnly !== true,
      field_class: binding === null ? 'signer' : 'merge',
      binding,
      role_key: roleKey,
      // Written on EVERY entry, not only the restricted ones. A layout where
      // the flag is sometimes absent makes "false" and "nobody said" the same
      // value, and the whole routing decision at submit time hangs on it.
      is_sensitive: isRestricted,
    });
  }

  // Reading order, so the review list runs down the page the way the document
  // does rather than in whatever order the annotations were written.
  fields.sort((a, b) => (
    (a.page - b.page)
    || (a.position_y - b.position_y)
    || (a.position_x - b.position_x)
  ));

  return {
    fields,
    restrictedFields: restricted,
    skippedOther: SKIP_ORDER
      .filter((reason) => (counts.get(reason) ?? 0) > 0)
      .map((reason) => ({
        reason,
        count: counts.get(reason) ?? 0,
        pages: Array.from(skipPages.get(reason) ?? []).sort((a, b) => a - b),
      })),
    unhandledPages: Array.from(unhandled).sort((a, b) => a - b),
    blockers: [],
  };
}
